import React, { useState } from "react";
import {
  TextField,
  Checkbox,
  Box,
  Typography,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Paper,
  styled,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
} from "@mui/material";
import Button from './Button';
import Grid from '@mui/material/Grid2';
import defaultBackground from '../../assets/contact-form-background.png';
import { grey } from "@mui/material/colors";
import { ContactFormProps } from "../../interfaces";
import ErrorOutlineIcon from '@mui/icons-material/Error';


const FormHeader = ({ title }: { title?: React.ReactNode }) => {
  if (title) return <>{title}</>;
  
  return (
    <Typography variant="subtitle8" sx={{textTransform: "uppercase", color: "darkPurple.500"}}>
      <Box component="span" sx={{
        marginRight: "8px",
        color: "lightPurple.500"
      }}>Ready</Box>
      <Box component="span">to get started? <br /> Let's chat!</Box>
    </Typography>
  );
};

// Input field component
const InputField = ({
  label,
  placeholder = "",
  required = false,
  type = "text",
  value,
  onChange,
  name,
  error
}: {
  label: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
  error?: string;
}) => {
  const isValid = value && value.trim() !== "" && !error;
  const borderColor = error ? "none" : 
                    isValid ? "2px solid #18DC81 !important" : "2px solid #321338";

  return (
  <Box sx={{
    display: "flex",
    padding: "0px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
    alignSelf: "stretch",
    width: "100%",
  }}>
    <Box sx={{
        display: "flex",
        alignItems: "flex-start",
        alignSelf: "stretch",
    }}>
      <Typography component="label" variant="body1" sx={{
        color: "darkPurple.500",
        textTransform: "uppercase",
      }}>
        {label}
        </Typography>
      {required && (
        <Typography component="span" variant="body1" sx={{
            color: "lightPurple.400"
        }}>
          *
        </Typography>
      )}
    </Box>
    <TextField
      type={type}
      placeholder={placeholder}
      fullWidth
      variant="outlined"
      required={required}
      value={value}
      onChange={onChange}
      name={name}
      sx={{
        marginTop: "0 !imporatant",
        '& .MuiOutlinedInput-root': {
          border: error ? "2px solid #F53D49 !important" : "none",
          '&:focus': {
            border: borderColor,
          },
          '&:hover fieldset': {
            border: borderColor,
          },
          '&.Mui-focused fieldset': {
            border: borderColor,
          },
          '& fieldset': {
            border: "none",
          },
        },
      }}
      slotProps={{
        input: { sx: { 
            color: "gray.300",
            padding: "0px",
            borderRadius: "14px",
            backgroundColor: "gray.100",
            alignSelf: "stretch",
            alignItems: "center",
            // border: borderColor,
         }, endAdornment: error && (
          <ErrorOutlineIcon color="error" 
          sx={{ 
            mr: 1, width: "20px", height: "20px", display: "flex",
            justifyContent: "center", alignItems: "center", gap: "0px"
          }} />
        ), },
        htmlInput: { sx: {
            fontVariant: "body1",
            textTransform: "uppercase",
        }},
      }}
    />
     {error && (
      <Typography 
        variant="body1" 
        color="error" 
        sx={{ 
          alignSelf: "flex-start",
          textTransform: "uppercase",
        }}
      >
        {error}
      </Typography>
    )}
  </Box>
)};

// Privacy checkbox component
const PrivacyCheckbox = ({ checked, onChange, error }: { 
  checked: boolean; 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
}) => (
  <Box sx={{
    display: "flex",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch"
  }}>
    <FormControlLabel sx={{
    marginLeft: "0px !important",
    padding: "8px 0px",
    gap: "8px"}}
      control={
        <Checkbox
          checked={checked}
          onChange={onChange}
          sx={{
            display: "flex",
            width: "24px",
            minWidth: "24px",
            height: "24px",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "6px",
            padding: "0px",
            backgroundColor: error ? "rgba(211, 47, 47, 0.1)" : "lightPurple.400",
            border: error ? "1px solid rgba(211, 47, 47, 0.5)" : "none",
          }}
          icon={<Box sx={{
            backgroundColor: "lightPurple.400",
            borderRadius: "6px",
            display: "flex",
            width: "24px",
            height: "24px",
            justifyContent: "center",
            alignItems: "center",
          }} />}
          checkedIcon={
            <Box sx={{
                backgroundColor: "lightPurple.400",
                borderRadius: "6px",
                display: "flex",
                width: "24px",
                height: "24px",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_72_1316)">
                  <path
                    d="M6 12.0005L10.2426 16.2431L18.7275 7.75781"
                    stroke="#321338"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </g>
                <defs>
                  <clipPath id="clip0_72_1316">
                    <rect width="24" height="24" fill="white"></rect>
                  </clipPath>
                </defs>
              </svg>
            </Box>
          }
        />
      }
      label={
        <Typography variant="body1" sx={{ 
            color: error ? "error.main" : "gray.300",
            textTransform: "uppercase",
          }}>
          I agree with the privacy policy and term of use
        </Typography>
      }
    />
    {error && (
      <Typography 
        variant="body1" 
        color="error" 
        sx={{ 
          alignSelf: "flex-start",
          ml: 4.5,
          textTransform: "uppercase",
          alignItems: "center",
        }}
      >
        Please accept the privacy policy
      </Typography>
    )}
  </Box>
);

// Predefined field configurations
const FORM_VARIANTS = {
  individual: [
    {
      name: "firstName",
      label: "First name",
      placeholder: "Enter first name",
      required: true,
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 1, sm: 1, md: 1 },
    },
    {
      name: "lastName",
      label: "Last name",
      placeholder: "Enter last name",
      required: true,
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 2, sm: 2, md: 3 },
    },
    {
      name: "email",
      label: "Email",
      placeholder: "Enter email",
      type: "email",
      required: true,
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs:3, sm: 3, md: 5 },
    },
    {
      name: "phoneNumber",
      label: "Phone number",
      placeholder: "Enter phone number",
      type: "tel",
      required: true,
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 4, sm: 4, md: 2 },
    },
    {
      name: "companyWebsite",
      label: "Company website",
      placeholder: "Enter company website",
      type: "url",
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs:5, sm: 5, md: 4 },
    },
  ],
  company: [
    {
      name: "firstName",
      label: "First name",
      placeholder: "Enter first name",
      required: true,
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 1, sm: 1, md: 1 },
    },
    {
      name: "lastName",
      label: "Last name",
      placeholder: "Enter last name",
      required: true,
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 2, sm: 2, md: 3 },
    },
    {
      name: "businessEmail",
      label: "Business email",
      placeholder: "Enter business email",
      type: "email",
      required: true,
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 3, sm: 3, md: 5 },
    },
    {
      name: "phoneNumber",
      label: "Phone number",
      placeholder: "Enter phone number",
      type: "tel",
      required: true,
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 4, sm: 4, md: 7 },
    },
    {
      name: "companyWebsite",
      label: "Company website",
      placeholder: "Enter company website",
      type: "url",
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 5, sm: 5, md: 2 },
    },
    {
      name: "industry",
      label: "Industry",
      placeholder: "Enter your industry",
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 6, sm: 6, md: 4 },
    },
    {
      name: "turnover",
      label: "Turnover",
      placeholder: "Enter your annual turnover",
      size: { xs: 12, sm: 12, md: 6 },
      order: { xs: 7, sm: 7, md: 6 },
    },
  ],
};

// Main component
function ReusableContactForm({
  variant = "individual",
  background = defaultBackground,
  title,
  onSubmit,
  customFields,
}: ContactFormProps) {
  const fields = customFields || FORM_VARIANTS[variant];
  
  // Create initial form data based on fields
  const initialFormData = fields.reduce((acc, field) => {
    acc[field.name] = "";
    return acc;
  }, {} as Record<string, string>);

  const [formData, setFormData] = useState(initialFormData);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [open, setOpen] = useState(false);

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const validateField = (name: string, value: string): string => {
    if (!value) return "";
    
    switch (name) {
      case "email":
      case "businessEmail":
        return validateEmail(value);
      case "phoneNumber":
        return validatePhone(value);
      default:
        return "";
    }
  };
  
  const validateEmail = (email: string): string => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? "" : "Invalid Email Format";
  };
  
  const validatePhone = (phone: string): string => {
    const phoneRegex = /^\+?[0-9\s\-()]{8,20}$/;
    return phoneRegex.test(phone) ? "" : "Invalid Phone Number Format";
  };
  

  const Puller = styled('div')(({ theme }) => ({
    width: 30,
    height: 6,
    backgroundColor: grey[300],
    borderRadius: 3,
    position: 'absolute',
    top: 8,
    left: 'calc(50% - 15px)',
    ...theme.applyStyles('dark', {
      backgroundColor: grey[900],
    }),
  }));

  const handleInputChange = (e: { target: { name: string; value: string; }; }) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (attemptedSubmit) {
      const error = validateField(name, value);
      setValidationErrors(prev => ({
        ...prev,
        [name]: value.trim() === "" && fields.find(f => f.name === name)?.required 
          ? "REQUIRED FIELD" 
          : error
      }));
    }
  };

  const handleCheckboxChange = (e: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
    setPrivacyChecked(e.target.checked);
  };

  const handleSubmit = (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    setAttemptedSubmit(true);

    const errors: Record<string, string> = {};
  
  fields.forEach(field => {
    const value = formData[field.name];
    
    if (field.required && (!value || value.trim() === "")) {
      errors[field.name] = "REQUIRED FIELD";
    } else if (value) {
      const formatError = validateField(field.name, value);
      if (formatError) {
        errors[field.name] = formatError.toUpperCase();
      }
    }
  });
  
  setValidationErrors(errors);

  const hasErrors = Object.keys(errors).length > 0;
  const isFormValid = !hasErrors && privacyChecked;

  if (!isFormValid || hasErrors) {
    return; 
  }

    const createMailtoLink = () => {
        const toEmail = "contact@epayr.finance"; 
        
        const subject = variant === "company" 
          ? "Business Inquiry from Website" 
          : "Individual Contact Form Submission";
        
        let bodyContent = "";
        Object.entries(formData).forEach(([key, value]) => {
          if (value) {
            const fieldName = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
            bodyContent += `${fieldName}: ${value}\n`;
          }
        });
        
        const mailtoUrl = `mailto:${toEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyContent)}`;
        
        return mailtoUrl;
      };

      const mailtoUrl = createMailtoLink();
    window.location.href = mailtoUrl;

    if (onSubmit) onSubmit(formData);
    setFormSubmitted(true);
    setOpen(true);

    // Reset form after submission
    setFormData(initialFormData);
    setPrivacyChecked(false);
    setAttemptedSubmit(false);
  };

  const handleModalToggle = () => {
    setOpen(!open);
  };

  return (
    <>
      <Box component="div" sx={{
        display: "flex",
        minHeight: {md: "712px", xs: "822px"},
        alignItems: {md: "center", xs: "flex-start"},
        flexDirection: {md: "row", xs: "column"},
        gap: {md: "50px", xs: "24px"},
        alignSelf: "stretch",
        padding: "12px"
      }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, sm: 12, md: 6 }}  sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "32px",
            flex: "1 0 0",
            order: { xs: 2, sm: 2, md: 1 },
        }}>
            <FormHeader title={title} />

            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2} sx={{
                display: "flex",
                alignItems: "flex-start",
                alignSelf: "stretch",
                flexDirection: {md: "row", xs: "column"},
              }}>
                {fields.map((field) => (
                  <Grid 
                    key={field.name}
                    size={field.size} 
                    sx={{ order: field.order }}
                  >
                    <InputField
                      label={field.label}
                      placeholder={field.placeholder}
                      required={field.required}
                      type={field.type}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleInputChange}
                      error={validationErrors[field.name]}
                    />
                  </Grid>
                ))}
                <Grid size={{ xs: 12 }} sx={{
                    order: 8
                }}>
                    <PrivacyCheckbox
                    checked={privacyChecked}
                    onChange={handleCheckboxChange}
                    error={attemptedSubmit && !privacyChecked}
      
                />
                </Grid>
                <Grid size={{xs: 12, sm: 12, md: 6}} sx={{
                    order: 9
                }}>
                <Button 
                    variant="lightPurple" 
                    type="submit" 
                    style={{ 
                        width: "100%",
                    }}>
                    SEND
                </Button>
            </Grid>
            {attemptedSubmit && (Object.keys(validationErrors).length > 0 || !privacyChecked) && (
      <Grid size={{ xs: 12 }} sx={{ order: 10, mt: 2 }}>
        <Typography 
          variant="body2" 
          color="error" 
          align="center"
          sx={{ textTransform: "uppercase", fontWeight: 500 }}
        >
          Please fill in all required fields correctly
        </Typography>
      </Grid>
    )}
              </Grid>
              {formSubmitted && (
                <Typography variant="body5" sx={{
                    color: "darkPurple.500",
                    textTransform: "uppercase",
                    marginTop: "16px",
                }}>
                  Thank you for your submission!
                </Typography>
              )}
            </form>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 6 }}
            sx={{
              backgroundImage: `url(${background})`,
              backgroundSize: "cover",
              order: { xs: 1, sm: 1, md: 2 },
              minHeight: {md: "617px", xs: "216px"},
            }}>
          </Grid>
        </Grid>
      </Box>
      
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onOpen={handleModalToggle}
          onClose={handleModalToggle}
          PaperProps={{
            sx: {
              background: '#FFF',
              boxShadow: '0px 4px 2px 0px rgba(0, 0, 0, 0.05)',
              borderRadius: '12px 12px 0px 0px',
            }
          }}
        >
          <Puller />
          <Box sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            padding: "90px 70px 70px 70px",
            gap: "32px",
          }}>
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "32px"
              }}
            >
              <Stack spacing={4} alignItems="center">
                <Typography align="center"
                  fontSize={{ xs: 24, sm: 36 }}
                  fontWeight={500}
                  textTransform={"uppercase"}
                  lineHeight={1.2}
                  textAlign={"center"}
                  fontFamily={"Manrope"}
                >
                  <Box component="span" sx={{ color: "#321338" }}>
                    Thank you{" "}
                  </Box>
                  <Box component="span" sx={{ color: "#9484BA" }}>
                    for getting in touch with us
                  </Box>
                </Typography>

                <Stack alignItems="center" spacing={0.5}>
                  <Typography
                    variant="h6"
                    align="center"
                    color="#818181"
                    fontSize={{ xs: 18, sm: 22 }}
                  >
                    WE WILL GET BACK TO YOU
                  </Typography>

                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography 
                      fontSize={{ xs: 18, sm: 22 }}
                      sx={{ color: "#818181", fontWeight: 500 }}
                    >
                      WITHIN THE NEXT
                    </Typography>
                    <Chip
                      label="48 HOURS"
                      sx={{
                        bgcolor: "#E9E5F3", // Light purple
                        color: "#6E5E9E",
                        fontWeight: 600,
                        fontSize: {sm: 18, xs: 14},
                        padding: "6px 14px",
                        borderRadius: "10px",
                      }}
                    />
                  </Stack>
                </Stack>
                <Button
                  variant="lime"
                  onClick={handleModalToggle}
                >
                  CLOSE
                </Button>
              </Stack>
            </Paper>
          </Box>
        </SwipeableDrawer>
      ) : (
        <Dialog open={open} onClose={handleModalToggle}>
          <DialogContent sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            padding: "90px 70px 70px 70px",
            gap: "32px",
          }}>
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "32px"
              }}
            >
              <Stack spacing={8} alignItems="center">
                <Typography align="center"
                  fontSize={{ xs: 24, sm: 36 }}
                  fontWeight={500}
                  textTransform={"uppercase"}
                  lineHeight={1.2}
                  textAlign={"center"}
                  fontFamily={"Manrope"}
                >
                  <Box component="span" sx={{ color: "#321338" }}>
                    Thank you{" "}
                  </Box>
                  <Box component="span" sx={{ color: "#9484BA" }}>
                    for getting in touch with us
                  </Box>
                </Typography>

                <Stack alignItems="center" spacing={0.5}>
                  <Typography
                    variant="h6"
                    align="center"
                    color="#818181"
                    fontSize={{ xs: 18, sm: 22 }}
                  >
                    WE WILL GET BACK TO YOU
                  </Typography>

                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography 
                      fontSize={{ xs: 18, sm: 22 }}
                      sx={{ color: "#818181", fontWeight: 500 }}
                    >
                      WITHIN THE NEXT
                    </Typography>
                    <Chip
                      label="48 HOURS"
                      sx={{
                        bgcolor: "#E9E5F3",
                        color: "#6E5E9E",
                        fontWeight: 600,
                        fontSize: {sm: 18, xs: 14},
                        padding: "6px 14px",
                        borderRadius: "10px",
                      }}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          </DialogContent>
          <DialogActions sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: "70px",
          }}>
            <Button
              onClick={handleModalToggle}
            >
              CLOSE
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export const IndividualContactForm = (props: Omit<ContactFormProps, 'variant'>) => (
  <ReusableContactForm variant="individual" {...props} />
);

export const BusinessContactForm = (props: Omit<ContactFormProps, 'variant'>) => (
  <ReusableContactForm variant="company" {...props} />
);

export default ReusableContactForm;